import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { becomeScreen as openAsScreen } from "../lib/becomeScreen";
import { apiErrorOf } from "../lib/api";
import { usePendingScreen } from "../lib/usePendingScreen";
import { MenuAccountItems } from "../control/components/AccountControl";
import { AuthModal } from "../control/components/AuthModal";
import { GoldDialog } from "../control/components/GoldDialog";
import { FapsDialog } from "../control/components/FapsDialog";
import { FAPS_AUTH_REASON, type FapsOpenSource } from "../lib/faps";
import { SiteBar } from "./SiteBar";
import { CodeCard } from "./CodeCard";
import { FapsPill } from "./FapsPill";
import { FapsCapNudge } from "./FapsCapNudge";
import { FapsUnseatedCard } from "./FapsUnseatedCard";
import { type GoldUpsellGate, trackGoldGate } from "../lib/gold";

/**
 * SiteBar, wired, for every page that is not itself a screen: the account control, this
 * tab's claimable-screen identity, and the code card behind the bubble.
 *
 * The screen surfaces do NOT use this — DisplayApp wires the bar itself, because a
 * screen's code belongs to the flow that page owns: the pending screen's code MINTS a
 * device for whoever redeems it, and a claimed screen's code ADOPTS the device the tab
 * already is (see DeviceScreen) — neither wants the ambient mint-and-navigate below.
 *
 * TEN SECONDS between polls, not the pairing page's two. On /screen somebody is standing
 * in front of the television mid-pairing and the claim should land fast; here the code is
 * ambient chrome on every open tab of the whole site, and the poll exists mostly to keep
 * the entry alive. Whoever does claim a browsing tab waits at most one tick.
 */
const AMBIENT_POLL_MS = 30_000;

export function SiteChrome({
  opacity,
  code = true,
}: {
  opacity?: number;
  /**
   * False wherever the tab is not playing anything: the Goontroller (a remote is the
   * thing that drives screens — announcing it as claimable would let a typed code
   * navigate the driver away mid-session) and every ControlApp page (the library,
   * Browse, the Feed… — a code there named a "screen" that showed nothing). Code-less
   * chrome skips the announce entirely, not just the bubble, so the tab never even
   * exists to claim. True stays for MintedScreen, the black frame on the way to a screen.
   */
  code?: boolean;
}) {
  /*
   * The mobile menu's sign-in dialog lives HERE, above the bar, not in the menu row
   * that opens it. SiteMenu dismisses itself on any mousedown outside its DOM node,
   * and the modal portals to document.body — outside by definition — so a modal owned
   * inside the menu died on the first tap into it. See MenuAccountItems.
   */
  /* Which door was opened, not merely whether one was: "login" is the Sign in row,
     "faps" is the phone-only Earn row, which wants register mode and the Faps reason —
     the same modal the pill opens at wider viewports. */
  const [authOpen, setAuthOpen] = useState<null | "login" | "faps">(null);
  // The Gold dialog obeys the same ownership rule as the sign-in modal above.
  const [goldOpen, setGoldOpen] = useState<GoldUpsellGate | null>(null);
  // The FAPS dialog too: the pill in the bar and the row in the menu both only REPORT
  // the tap; the dialog is owned here, above the menu that would unmount it.
  const [fapsOpen, setFapsOpen] = useState<FapsOpenSource | null>(null);
  // The account rows are the ☰'s TOP group at every width now — the desktop bubble is
  // gone, so this is the one place who-you-are renders in the chrome.
  const account = (
    <MenuAccountItems
      onSignIn={() => setAuthOpen("login")}
      onGold={setGoldOpen}
      onFaps={() => setFapsOpen("menu")}
      onEarnFaps={() => setAuthOpen("faps")}
    />
  );
  const faps = <FapsPill onOpen={() => setFapsOpen("pill")} />;
  const auth = (
    <>
      {authOpen ? (
        <AuthModal
          initialMode={authOpen === "faps" ? "register" : "login"}
          reason={authOpen === "faps" ? FAPS_AUTH_REASON : undefined}
          onClose={() => setAuthOpen(null)}
        />
      ) : null}
      {goldOpen ? <GoldDialog source={goldOpen} onClose={() => setGoldOpen(null)} /> : null}
      {fapsOpen ? <FapsDialog source={fapsOpen} onClose={() => setFapsOpen(null)} /> : null}
      {/* The crown's cap moment, sold — beside the dialog it opens. */}
      <FapsCapNudge onGoPremium={() => setGoldOpen("faps_cap")} />
      {/* The bidding sink's other side: a square of yours was taken — take it back. */}
      <FapsUnseatedCard />
    </>
  );
  if (!code) {
    return (
      <>
        <SiteBar opacity={opacity} faps={faps} account={account} />
        {auth}
      </>
    );
  }
  return (
    <ClaimableChrome opacity={opacity} faps={faps} account={account} auth={auth} onGold={setGoldOpen} />
  );
}

function ClaimableChrome({
  opacity,
  faps,
  account,
  auth,
  onGold,
}: {
  opacity?: number;
  /** The FAPS pill, built by the chrome that owns its dialog. */
  faps: ReactNode;
  /** The ☰'s top group — the account rows the wired chrome builds. */
  account: ReactNode;
  auth: ReactNode;
  /** Open the Gold dialog the wired chrome owns — the screens cap lands here. */
  onGold: (gate: GoldUpsellGate) => void;
}) {
  const navigate = useNavigate();
  const { code, error } = usePendingScreen(AMBIENT_POLL_MS);
  const [showCode, setShowCode] = useState(false);
  const [becoming, setBecoming] = useState(false);

  /*
   * Turn this browser into a screen, no code involved: this browser already HAS the
   * account, which is how it is showing this page. Moved here from TopNav's touch-only
   * button — the card has room to offer it to everybody.
   *
   * No view is asked for, because this page is not playing one. The server points the new
   * device at a random quick setup rather than handing back a blank screen; see
   * ensureShowing in server/src/routes/devices.ts.
   *
   * `?code=1`: the person who pressed this was looking at the code card, so the screen
   * they just made opens on its own — they came to pair, and the code is how. See
   * usePairing in DeviceScreen.
   */
  const becomeScreen = async () => {
    setBecoming(true);
    try {
      const device = await openAsScreen();
      navigate(`/view/d/${device.id}?code=1`);
    } catch (e) {
      setBecoming(false);
      // The screens cap used to die here in silence — the card's button just stopped
      // being busy. It is a gate like any other, so it gets the dialog like any other.
      if (apiErrorOf(e)?.goldUpsell) {
        trackGoldGate("screens");
        onGold("screens");
      }
    }
  };

  return (
    <>
      <SiteBar
        opacity={opacity}
        code={{
          // The placeholder rather than nothing: the bubble is the same size before and
          // after the code lands, so the bar does not reflow the moment it arrives.
          text: code ?? "····",
          shown: showCode,
          onToggle: () => setShowCode((s) => !s),
          // The chip body is a door on every non-screen page: /screen wears this same
          // code (usePendingScreen's identity survives the navigation), so the click
          // reads as "expand this" rather than "go somewhere new". `?code=1`: a bare
          // /screen greets with the intro and keeps the card closed (!142), so the
          // door asks for the card it promised (Parker 2026-10-21).
          onOpen: () => navigate("/screen?code=1"),
        }}
        faps={faps}
        account={account}
      />
      {showCode ? (
        <CodeCard
          code={code}
          error={error}
          onClose={() => setShowCode(false)}
          becomeScreen={{ busy: becoming, onGo: () => void becomeScreen() }}
        />
      ) : null}
      {auth}
    </>
  );
}
